import { useEffect, useRef } from "react";
import { SalesforceBundle } from "~/api/models/Bundle";
import { UseInitializeProductsArgs } from "~/hooks/useInitializeProducts";
import { useBundles } from "./useBundles";
import useSkinProducts from "./useSkinProducts";

const useInitializeBundles = ({
  opportunity,
  loadingOpportunity,
  onComplete,
}: UseInitializeProductsArgs) => {
  const hasInitialized = useRef(false);
  const lastOpportunityId = useRef<string | null>(null);
  const { bundles } = useSkinProducts();
  const { setSelectedBundles, selectedBundles } = useBundles();

  // Reset initialization when opportunity changes
  useEffect(() => {
    if (opportunity?.id !== lastOpportunityId.current) {
      hasInitialized.current = false;
      lastOpportunityId.current = opportunity?.id || null;
    }
  }, [opportunity?.id]);

  useEffect(() => {
    // Initialize bundles when they exist in the opportunity
    if (
      !hasInitialized.current &&
      !loadingOpportunity &&
      opportunity?.getBundleId() &&
      bundles &&
      bundles.length > 0 &&
      selectedBundles.length === 0 // Only run if no bundles are already selected
    ) {
      if (opportunity && opportunity.bundles.length > 0) {
        const selectedBundles = opportunity.bundles
          .map((bundleId) => {
            const foundBundle = bundles?.find((b) => b.id === bundleId);
            return foundBundle;
          })
          .filter((bundle): bundle is SalesforceBundle => bundle !== undefined);

        if (selectedBundles.length > 0) {
          setSelectedBundles(selectedBundles);
          hasInitialized.current = true;
          onComplete?.();
        }
      }
    } else if (!loadingOpportunity && !opportunity?.getBundleId()) {
      // If there's no bundle, mark as completed immediately
      if (!hasInitialized.current) {
        hasInitialized.current = true;
        onComplete?.();
      }
    }
  }, [
    loadingOpportunity,
    opportunity,
    bundles,
    setSelectedBundles,
    selectedBundles.length,
    onComplete,
  ]);
};

export default useInitializeBundles;
